import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefundsApi } from '../../clients';
import { debounce } from 'lodash';
import { APPROVAL_STATUS, REFUND_TYPES } from '@/utility';
import { REFUND_METHOD, REFUND_PATH } from '@/utility';

export const useGetRefunds = ({ defaultValues, refundLocation }) => {
  const [state, setState] = useState({
    refunds: [],
    loading: true,
    startDate: '',
    endDate: '',
    dateFilterType: 'created_at',
    facility: '',
    facilityGroupId: '',
    search: '',
    debouncedSearch: '',
    paymentMethod: '',
    subStatus: '',
    selectedBusinessUnit: '',
    transactionStatus: '',
    additionalInfo: '',
    approvalStatus: '',
    offSet: 0,
    hasMore: true,
    payeeCategory: '',
    status: '',
    sortBy: { name: 'created_at', order: -1 },
    isError: false,
    selectedDataMappings: [],
    quickFilter: '',
    smartpayablesId: '',
    hasFilesAttached: false,
    customPaymentTypeId: '',
    hasPayImmediately: false,
    ...defaultValues,
  });

  const setPartialState = (updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  };

  const fetchRefunds = async (filters, sort) => {
    setPartialState({ loading: true });
    try {
      let response;
      if (refundLocation === REFUND_PATH.APPROVALS) {
        response = await RefundsApi.getRefundsForApproval(filters, sort);
      } else if (refundLocation === REFUND_PATH.PROCESSING_EXCEPTIONS) {
        response = await RefundsApi.getRefundsForException(filters, sort);
      } else if (refundLocation === REFUND_PATH.COMMUNICATION_EXCEPTIONS) {
        response = await RefundsApi.getRefundsForExceptionCommunication(
          filters,
          sort,
        );
      } else {
        response = await RefundsApi.getRefundsForFacility(filters, sort);
      }
      const { data } = response;

      if (filters.offSet === 0) {
        setPartialState({ refunds: data.refunds });
      } else {
        setState((prevState) => ({
          ...prevState,
          refunds: [...prevState.refunds, ...data.refunds],
        }));
      }
      setPartialState({ hasMore: data.refunds.length > 0 });
    } catch (error) {
      console.error('Error fetching refunds', error);
    } finally {
      setPartialState({ loading: false });
    }
  };

  const handleSearch = useCallback(
    debounce((searchValue) => {
      setPartialState({ debouncedSearch: searchValue });
    }, 1000),
    [],
  );

  useEffect(() => {
    const filters = {
      payeeCategory: state.payeeCategory,
      status: state.status,
      startDate: state.startDate,
      dateFilterType: state.dateFilterType,
      endDate: state.endDate,
      facility: state.facility,
      facilityGroupId: state.facilityGroupId,
      search: state.search,
      paymentMethod: state.paymentMethod,
      subStatus: state.subStatus,
      selectedBusinessUnit: state.selectedBusinessUnit,
      transactionStatus: state.transactionStatus,
      additionalInfo: state.additionalInfo,
      approvalStatus: state.approvalStatus,
      offSet: state.offSet,
      isError: state.isError,
      selectedDataMappings: state.selectedDataMappings,
      smartpayablesId: state.smartpayablesId,
      hasFilesAttached: state.hasFilesAttached,
      customPaymentTypeId: state.customPaymentTypeId,
      hasPayImmediately: state.hasPayImmediately,
    };
    const sort = {
      sortBy: state.sortBy,
    };
    fetchRefunds(filters, sort).catch((error) =>
      console.error('Error fetching refunds', error),
    );
  }, [
    state.payeeCategory,
    state.status,
    state.startDate,
    state.dateFilterType,
    state.endDate,
    state.facility,
    state.facilityGroupId,
    state.debouncedSearch,
    state.paymentMethod,
    state.subStatus,
    state.selectedBusinessUnit,
    state.transactionStatus,
    state.additionalInfo,
    state.approvalStatus,
    state.offSet,
    state.sortBy.name,
    state.sortBy.order,
    state.isError,
    state.selectedDataMappings,
    state.smartpayablesId,
    state.hasFilesAttached,
    state.customPaymentTypeId,
    state.hasPayImmediately,
  ]);

  useEffect(() => {
    setPartialState({ offSet: 0 });
  }, [
    state.payeeCategory,
    state.status,
    state.startDate,
    state.dateFilterType,
    state.endDate,
    state.facility,
    state.facilityGroupId,
    state.debouncedSearch,
    state.paymentMethod,
    state.subStatus,
    state.selectedBusinessUnit,
    state.transactionStatus,
    state.additionalInfo,
    state.approvalStatus,
    state.sortBy.name,
    state.sortBy.order,
    state.isError,
    state.selectedDataMappings,
    state.smartpayablesId,
    state.hasFilesAttached,
    state.customPaymentTypeId,
    state.hasPayImmediately,
  ]);

  const refetchRefunds = useCallback(() => {
    setPartialState({ offSet: 0 });
    const filters = {
      payeeCategory: state.payeeCategory,
      status: state.status,
      startDate: state.startDate,
      dateFilterType: state.dateFilterType,
      endDate: state.endDate,
      facility: state.facility,
      facilityGroupId: state.facilityGroupId,
      search: state.search,
      paymentMethod: state.paymentMethod,
      subStatus: state.subStatus,
      selectedBusinessUnit: state.selectedBusinessUnit,
      transactionStatus: state.transactionStatus,
      additionalInfo: state.additionalInfo,
      approvalStatus: state.approvalStatus,
      offSet: 0,
      isError: state.isError,
      selectedDataMappings: state.selectedDataMappings,
      smartpayablesId: state.smartpayablesId,
      hasFilesAttached: state.hasFilesAttached,
      customPaymentTypeId: state.customPaymentTypeId,
      hasPayImmediately: state.hasPayImmediately,
    };
    const sort = {
      sortBy: state.sortBy,
    };
    fetchRefunds(filters, sort).catch((error) =>
      console.error('Error fetching refunds', error),
    );
  }, [
    state.payeeCategory,
    state.status,
    state.startDate,
    state.dateFilterType,
    state.endDate,
    state.facility,
    state.facilityGroupId,
    state.debouncedSearch,
    state.paymentMethod,
    state.subStatus,
    state.selectedBusinessUnit,
    state.transactionStatus,
    state.additionalInfo,
    state.approvalStatus,
    state.offSet,
    state.sortBy.name,
    state.sortBy.order,
    state.isError,
    state.selectedDataMappings,
    state.smartpayablesId,
    state.hasPayImmediately,
  ]);

  const payments = useMemo(() => {
    return state.refunds.map((payment) => {
      payment.isChecked = false;
      payment.refund_amount = +payment.refund_amount;
      payment.refund_method_string =
        payment.refund_method == REFUND_METHOD.PAYPAL
          ? 'Paypal'
          : payment.refund_method == REFUND_METHOD.VENMO
          ? 'Venmo'
          : payment.refund_method == REFUND_METHOD.GIFTCARD
          ? 'Gift Card'
          : payment.refund_method == REFUND_METHOD.CHECK
          ? 'Check'
          : payment.refund_method == REFUND_METHOD.DONATION
          ? 'Donations'
          : payment.refund_method == REFUND_METHOD.ACH
          ? 'ACH'
          : payment.refund_method == 0
          ? 'Escheated'
          : '';

      payment.approval_status_string =
        payment.approval_status === APPROVAL_STATUS.WAITING_FOR_APPROVAL
          ? 'Awaiting Approval'
          : payment.approval_status === APPROVAL_STATUS.APPROVED
          ? 'Approved'
          : payment.approval_status === APPROVAL_STATUS.DECLINED
          ? 'Declined'
          : 'N/A';

      payment.refund_type_string =
        payment.refund_type === REFUND_TYPES.REGULAR
          ? 'Consumer Payment'
          : payment.refund_type === REFUND_TYPES.INSURANCE
          ? 'Insurance Payment'
          : payment.refund_type === REFUND_TYPES.BUSINESS
          ? 'Business Payment'
          : '';
      return payment;
    });
  }, [state.refunds]);

  return {
    ...state,
    setPartialState,
    payments,
    refetchRefunds,
    handleSearch,
  };
};
